 'use client'
import { useCallback, useRef, useState } from 'react';

import type { Toast } from '../../components/shared/Toast/types';

export type { Toast }

const AUTO_DISMISS_MS = 5 * 1000;

export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismissalTimers = useRef<Record<string, NodeJS.Timeout>>({});

  const removeToast = useCallback((id: string) => {
    const existingTimer = dismissalTimers.current[id];
    if (existingTimer) {
      clearTimeout(existingTimer);
      delete dismissalTimers.current[id];
    }

    setToasts((prev) => prev.filter((toastEntry) => toastEntry.id !== id));
  }, []);

  const toast = useCallback(
    ({ title, description, variant = 'default' }: Omit<Toast, 'id'>) => {
      const id = crypto.randomUUID();
      const toastEntry: Toast = { id, title, description, variant };
      
      setToasts((prev) => [...prev, toastEntry]);

      dismissalTimers.current[id] = setTimeout(() => {
        removeToast(id);
      }, AUTO_DISMISS_MS);

      return { id };
    },
    [removeToast],
  );

  const clearAllToasts = useCallback(() => {
    Object.keys(dismissalTimers.current).forEach((id) => {
      const timer = dismissalTimers.current[id];
      if (timer) {
        clearTimeout(timer);
        delete dismissalTimers.current[id];
      }
    });
    setToasts([]);
  }, []);

  return {
    toast,
    toasts,
    removeToast,
    clearAllToasts,
  };
}